'use client';

import React from 'react';
import { Package, MoreHorizontal, User, Scissors, ShoppingBag } from 'lucide-react';

export interface SaleRecord {
  id: string;
  createdAt: string;
  subtotal: number;
  discount: number;
  total: number;
  paymentChannel: string;
  notes?: string;
  client?: { id: string; name: string; phone: string };
  staff?: { id: string; name: string };
  appointment?: { id: string; serviceName: string; dateStr: string; startTime: string };
  items: Array<{
    id?: string;
    name: string;
    quantity: number;
    unitPrice: number;
    subtotal: number;
    productId?: string | null;
    serviceId?: string | null;
  }>;
}

interface SalesTableProps {
  sales: SaleRecord[];
  loading: boolean;
  onOpenChargeModal: () => void;
  onViewDetails?: (sale: SaleRecord) => void;
}

export default function SalesTable({
  sales,
  loading,
  onOpenChargeModal,
  onViewDetails
}: SalesTableProps) {
  if (loading) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm font-medium text-slate-500">Cargando transacciones en vivo...</p>
      </div>
    );
  }

  if (sales.length === 0) {
    return (
      <div className="bg-white border border-slate-200/90 rounded-3xl p-16 text-center shadow-sm flex flex-col items-center justify-center min-h-[340px]">
        <div className="w-16 h-16 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400 mb-4">
          <Package className="w-8 h-8 stroke-[1.2]" />
        </div>
        <h3 className="text-base md:text-lg font-bold text-slate-800">
          Sin ventas para mostrar.
        </h3>
        <p className="text-xs md:text-sm text-slate-400 mt-1 max-w-sm">
          Creá tu primera venta con el botón de arriba.
        </p>
        <button
          onClick={onOpenChargeModal}
          className="mt-5 px-4 py-2 bg-[#047857] hover:bg-[#065f46] text-white text-xs font-bold rounded-xl shadow-sm transition transform active:scale-95"
        >
          + Cobrar primer cliente
        </button>
      </div>
    );
  }

  const getChannelBadge = (channel: string) => {
    switch (channel) {
      case 'EFECTIVO':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/60">💵 Efectivo</span>;
      case 'MERCADOPAGO':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200/60">📱 Mercado Pago</span>;
      case 'TRANSFERENCIA':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-violet-50 text-violet-700 border border-violet-200/60">🏦 Transferencia</span>;
      case 'DEBITO':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200/60">💳 Débito</span>;
      case 'CREDITO':
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/60">💳 Crédito</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">🏷️ {channel}</span>;
    }
  };

  return (
    <div className="bg-white border border-slate-200/90 rounded-3xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="bg-slate-50/80 border-b border-slate-200/80 text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="py-3 px-4">Fecha y Hora</th>
              <th className="py-3 px-4">Cliente</th>
              <th className="py-3 px-4">Atendido por</th>
              <th className="py-3 px-4">Detalle de Ítems</th>
              <th className="py-3 px-4">Método</th>
              <th className="py-3 px-4 text-right">Total</th>
              <th className="py-3 px-4 text-center">Estado</th>
              <th className="py-3 px-3 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sales.map((sale) => (
              <tr 
                key={sale.id} 
                className="hover:bg-slate-50/60 transition group cursor-pointer"
                onClick={() => onViewDetails && onViewDetails(sale)}
              >
                {/* Fecha y Hora */}
                <td className="py-3.5 px-4 font-mono text-slate-700">
                  {new Date(sale.createdAt).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}{' · '}
                  <span className="font-semibold">
                    {new Date(sale.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </td>

                {/* Cliente */}
                <td className="py-3.5 px-4">
                  <div className="font-bold text-slate-900 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    <span>{sale.client?.name || 'Cliente casual'}</span>
                  </div>
                  {sale.client?.phone && (
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5">
                      {sale.client.phone}
                    </div>
                  )}
                </td>

                {/* Staff */}
                <td className="py-3.5 px-4 font-medium text-slate-700">
                  {sale.staff?.name || 'Caja Central'}
                </td>

                {/* Ítems */}
                <td className="py-3.5 px-4">
                  <div className="flex flex-col gap-1 max-w-xs">
                    {sale.items.map((it, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-[11px]">
                        {it.serviceId ? (
                          <Scissors className="w-3 h-3 text-emerald-600 shrink-0" />
                        ) : (
                          <ShoppingBag className="w-3 h-3 text-sky-600 shrink-0" />
                        )}
                        <span className="font-bold text-slate-800">{it.quantity}x</span>
                        <span className="truncate text-slate-600">{it.name}</span>
                      </div>
                    ))}
                  </div>
                </td>

                {/* Método */}
                <td className="py-3.5 px-4">
                  {getChannelBadge(sale.paymentChannel)}
                </td>

                {/* Total */}
                <td className="py-3.5 px-4 text-right font-black text-sm text-slate-900">
                  ${sale.total.toLocaleString('es-AR')}
                </td>

                {/* Estado */}
                <td className="py-3.5 px-4 text-center">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                    Entregada
                  </span>
                </td>

                {/* Acciones */}
                <td className="py-3.5 px-3 text-center">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewDetails && onViewDetails(sale);
                    }}
                    className="p-1 rounded-lg hover:bg-slate-200/60 text-slate-400 hover:text-slate-700 transition"
                    title="Ver detalle del comprobante"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
